import React, { useState, useRef } from "react";
import { Col, Modal, Row } from "react-bootstrap";
import Card from "../Card/Card";
import Button from "components/CustomButton/CustomButton";
import { DateTime } from "luxon";
import FullCalendar from "@fullcalendar/react"; // must go before plugins
import dayGridPlugin from "@fullcalendar/daygrid"; // a plugin!
import interactionPlugin from "@fullcalendar/interaction"; // needed for dayClick
import momentPlugin from "@fullcalendar/moment-timezone"; // needed for dayClick
import "./SpecificDateCalendar.scss";
import SpecificDateForm from "./SpecificDateForm";
import { collections } from "services";
import { useEffect } from "react";
import { db } from "firebase.js";
import { isBusinessWideShift } from "helpers/effectiveHours.helper";

const SpecificDateCalendar = ({
  showSpecificDateModal,
  closeModal,
  business,
  resources = [],
  // Set when opened from a single resource's own hours editor (the Add/Edit Resource
  // modal): the calendar only shows and edits that resource's overrides, so there is
  // no way to see or touch business-wide or other-resource hours from here.
  resourceId = null,
  resourceName = ""
}) => {
  let unsubscribe = null;
  const calendarRef = useRef();
  const [showDateForm, setShowDateForm] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const isResourceLocked = !!resourceId;
  const visibleEvents = isResourceLocked ? events.filter(e => e.event.resourceId === resourceId) : events;

  // Clicking an empty day always targets the business-wide override. Per-resource
  // overrides are reached by clicking their own chip, so a date can carry several
  // without this becoming ambiguous. With no resources (restaurants) every event is
  // business-wide and this behaves exactly as it did before. When locked to a single
  // resource, the only override that could exist for that date is that resource's own.
  const onSelect = selectionInfo => {
    setSelectedEvent(
      isResourceLocked
        ? getEventsForDate(selectionInfo.start)[0] || null
        : getBusinessWideEventForDate(selectionInfo.start)
    );
    setSelectedDate(selectionInfo.start);
    setShowDateForm(true);
  };

  const eventClicked = e => {
    // Use the event that was actually clicked — resolving by date again would open
    // the business-wide override no matter which chip was picked.
    const clicked = e.event.extendedProps?.event;
    setSelectedEvent(clicked ? { event: clicked } : getBusinessWideEventForDate(e.event.start));
    setSelectedDate(e.event.start);
    setShowDateForm(true);
  };

  const closeDateForm = () => {
    setSelectedDate(null);
    setShowDateForm(false);
  };

  useEffect(() => {
    if (unsubscribe) {
      unsubscribe();
    }
    unsubscribe = db
      .collection(collections.businesses)
      .doc(business.businessId)
      .collection(collections.shifts)
      .where("date", ">=", DateTime.now().startOf("day").toJSDate())
      .where("accountId", "==", business.accountId)
      .onSnapshot(snapshot => {
        const events = [];
        snapshot.forEach(doc => {
          let e = { id: doc.id, ...doc.data() };
          events.push(createEvent(e));
        });
        setEvents(events);
      });
    return () => {
      unsubscribe();
    };
    //fetch hours and specific dates
  }, [business.id]);

  // Per-resource overrides are named and muted so the business-wide rows stay
  // visually dominant. Without resources nothing is scoped and titles are unchanged.
  // Locked mode already shows only one resource's events, so the prefix would be
  // redundant — and `resources` isn't necessarily passed in that mode anyway.
  const getScopeLabel = data => {
    if (!data.resourceId || isResourceLocked) {
      return "";
    }
    return `${resources.find(r => r.id === data.resourceId)?.name || "Deleted resource"}: `;
  };

  const createEvent = data => {
    const scope = getScopeLabel(data);
    const start = DateTime.fromJSDate(data.date.toDate(), { zone: business.settings?.timezone })
      .startOf("day")
      .toISODate();

    if (data.closed) {
      return {
        title: `${scope}No Reservations`,
        start,
        event: data,
        color: data.resourceId ? "#a45b64" : "#dc3545"
      };
    } else {
      const begin = DateTime.fromFormat(data.begin, "HH:mm");
      const end = DateTime.fromFormat(data.end, "HH:mm");
      return {
        title: `${scope}${begin.toFormat("h:mma")} - ${end.toFormat("h:mma")}`,
        start,
        event: data,
        ...(data.resourceId ? { color: "#6c757d" } : {})
      };
    }
  };

  const getEventsForDate = date => {
    if (!date) {
      return [];
    }
    return visibleEvents.filter(e => {
      return DateTime.fromJSDate(date).startOf("day").toISO() === DateTime.fromISO(e.start).startOf("day").toISO();
    });
  };

  const getBusinessWideEventForDate = date => getEventsForDate(date).find(e => isBusinessWideShift(e.event));

  const shouldAllowSelection = e => {
    let startDay = DateTime.now().startOf("day").toJSDate();
    if (e.start.getTime() >= startDay.getTime() && e.end.getTime() / 1000 - e.start.getTime() / 1000 <= 86400) {
      return true;
    }
  };

  return (
    <>
      <Modal dialogClassName="specific-date-modal" show={showSpecificDateModal} onHide={closeModal}>
        <Card
          title={
            isResourceLocked
              ? `Select date to add specific hours for ${resourceName}`
              : "Select date to add specific hours to"
          }
          content={
            <>
              <Row>
                <Col>
                  <FullCalendar
                    ref={calendarRef}
                    timeZone={business.settings?.timezone}
                    height={600}
                    themeSystem="bootstrap"
                    editable={false}
                    droppable={false}
                    plugins={[dayGridPlugin, interactionPlugin, momentPlugin]}
                    initialView="dayGridMonth"
                    selectable={true}
                    eventStartEditable={true}
                    eventClick={eventClicked}
                    dayCellDidMount={e => {
                      if (e.isPast) {
                        e.el.classList.add("fc-disabled");
                      }
                    }}
                    select={onSelect}
                    selectAllow={shouldAllowSelection}
                    eventAllow={shouldAllowSelection}
                    events={visibleEvents}
                    visibleRange={{
                      start: "2022-11-22",
                      end: "2023-03-25"
                    }}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <div className="specific-date-footer__cancel">
                    <Button onClick={closeModal} bsStyle="info" pullRight fill type="submit">
                      Done
                    </Button>
                  </div>
                </Col>
              </Row>
            </>
          }
        ></Card>
      </Modal>
      {showDateForm && (
        <SpecificDateForm
          onHide={closeDateForm}
          business={business}
          date={selectedDate}
          selectedEvent={selectedEvent}
          resources={resources}
          lockedResourceId={resourceId}
          lockedResourceName={resourceName}
          existingEvents={getEventsForDate(selectedDate)}
          showDateForm={showDateForm}
        ></SpecificDateForm>
      )}
    </>
  );
};

export default SpecificDateCalendar;
